/* Adaptable mode data: the attached sales vaults and every item in them, with
   optimistic per-item saves and AI describe (which also sets the stage). */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../../api/client';
import type { OfVault, SalesAttachedVault, SalesMediaItem } from '../../../../api/client';
import * as salesCache from '../../Sales/salesCache';
import { isVideoItem } from '../../SalesLadder/MediaBadge';
import { canDescribe, type PoolItem } from './model';

const PAGE = 48;
const BULK_MAX = 200;

type Track = <R>(fn: () => Promise<R>) => Promise<R>;

export interface SalesPool {
  attached: SalesAttachedVault[];
  attachedLoading: boolean;
  items: Record<string, PoolItem[]>;
  loadingVaults: Set<string>;
  error: string | null;
  credits: number | null;
  analysing: Set<string>;
  attach: (v: OfVault) => void;
  detach: (id: string) => void;
  /** Set a field on items (optimistically) and save each one. */
  setField: (its: PoolItem[], p: { sales_stage?: string | null }) => void;
  describe: (its: PoolItem[]) => void;
}

/** Run `tasks` with at most `n` in flight. */
async function limited(tasks: (() => Promise<unknown>)[], n: number) {
  const q = [...tasks];
  await Promise.all(Array.from({ length: Math.min(n, q.length) }, async () => {
    for (let f = q.shift(); f; f = q.shift()) await f().catch(() => {});
  }));
}

export function useSalesPool(accountId: string, enabled: boolean, track: Track): SalesPool {
  const [attached, setAttached] = useState<SalesAttachedVault[]>([]);
  const [attachedLoading, setAttachedLoading] = useState(true);
  const [items, setItems] = useState<Record<string, PoolItem[]>>({});
  const [loadingVaults, setLoadingVaults] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [analysing, setAnalysing] = useState<Set<string>>(new Set());
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const queue = useRef<Promise<void>>(Promise.resolve());
  const asked = useRef<Set<string>>(new Set());

  // Load a vault's media fully, one vault at a time (paging every vault in
  // parallel overloads the upstream media proxy). The localStorage cache makes
  // a reload instant.
  const loadVault = useCallback((v: SalesAttachedVault) => {
    if (asked.current.has(v.id)) return;
    asked.current.add(v.id);
    const tag = (list: SalesMediaItem[]): PoolItem[] => list.map((it) => ({ ...it, vault_id: v.id, vault_name: v.vault_name }));
    const cached = salesCache.readVaultCache(accountId, v.id);
    if (cached && !cached.hasMore) {
      setItems((m) => ({ ...m, [v.id]: tag(cached.items) }));
      return;
    }
    setLoadingVaults((s) => new Set(s).add(v.id));
    queue.current = queue.current.then(async () => {
      let list: SalesMediaItem[] = [];
      let offset = 0;
      try {
        for (;;) {
          const p = await api.fetchSalesVaultMedia(accountId, v.id, offset, PAGE);
          const seen = new Set(list.map((i) => i.id));
          list = [...list, ...p.list.filter((i) => !seen.has(i.id))];
          setItems((m) => ({ ...m, [v.id]: tag(list) }));
          if (!p.has_more) break;
          offset = p.next_offset;
        }
        salesCache.writeVaultCache(accountId, v.id, { items: list, hasMore: false, nextOffset: offset });
      } catch (e) {
        asked.current.delete(v.id);
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoadingVaults((s) => { const n = new Set(s); n.delete(v.id); return n; });
      }
    });
  }, [accountId]);

  const reload = useCallback(async () => {
    setAttachedLoading(true);
    try {
      setAttached(await api.listAttachedSalesVaults(accountId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAttachedLoading(false);
    }
  }, [accountId]);

  useEffect(() => { if (enabled) void reload(); }, [enabled, reload]);
  useEffect(() => { if (enabled) attached.forEach(loadVault); }, [enabled, attached, loadVault]);
  useEffect(() => {
    if (!enabled) return;
    api.getDescriptionGenerationQuota(accountId).then((q) => setCredits(q.remaining_generations), () => {});
  }, [enabled, accountId]);

  /** Merge server fields into items and write the vault cache through. */
  const patch = useCallback((vaultId: string, id: string, p: Partial<SalesMediaItem>) => {
    setItems((m) => {
      const list = (m[vaultId] ?? []).map((it) => (it.id === id ? { ...it, ...p } : it));
      const c = salesCache.readVaultCache(accountId, vaultId);
      if (c) salesCache.writeVaultCache(accountId, vaultId, { ...c, items: c.items.map((it) => (it.id === id ? { ...it, ...p } : it)) });
      return { ...m, [vaultId]: list };
    });
  }, [accountId]);

  const setField: SalesPool['setField'] = (its, p) => {
    its.forEach((it) => patch(it.vault_id, it.id, p));
    void limited(its.map((it) => () => track(() => api.updateSalesItem(accountId, it.vault_id, it.id, p))), 4);
  };

  const mark = (ids: string[], on: boolean) => setAnalysing((s) => { const n = new Set(s); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; });

  const describe: SalesPool['describe'] = (its) => {
    const todo = its.filter(canDescribe);
    if (!todo.length) return;
    setError(null);
    mark(todo.map((i) => i.id), true);
    const apply = (vid: string, r: { id: string; description: string | null; sales_stage: string | null }) =>
      patch(vid, r.id, { description: r.description, sales_stage: r.sales_stage });
    if (todo.length === 1) {
      const it = todo[0]!;
      void api.generateSalesItemDescription(accountId, it.vault_id, it.id, { thumb_url: it.thumb_url, type: it.type }).then(
        (r) => { apply(it.vault_id, { id: it.id, ...r }); setCredits(r.remaining_generations); },
        (e) => setError(e instanceof Error ? e.message : String(e)),
      ).finally(() => mark([it.id], false));
      return;
    }
    const byVault = new Map<string, PoolItem[]>();
    todo.forEach((it) => byVault.set(it.vault_id, [...(byVault.get(it.vault_id) ?? []), it]));
    for (const [vid, list] of byVault) {
      for (let i = 0; i < list.length; i += BULK_MAX) {
        const chunk = list.slice(i, i + BULK_MAX);
        void api.bulkDescribeVault(accountId, vid, {
          items: chunk.map((it) => ({ content_id: it.id, thumb_url: it.thumb_url, video_url: isVideoItem(it.type) ? it.full_url || it.preview_url : undefined, type: it.type, duration: it.duration })),
        }).then(
          (r) => { r.results.forEach((u) => apply(vid, u)); setCredits(r.remaining_generations); },
          (e) => setError(e instanceof Error ? e.message : String(e)),
        ).finally(() => mark(chunk.map((c) => c.id), false));
      }
    }
  };

  const attach = (v: OfVault) => {
    const row = { id: v.id, vault_name: v.vault_name };
    setAttached((a) => [...a, row]);
    void track(() => api.attachSalesVault(accountId, v.id)).catch(() => {});
  };
  const detach = (id: string) => {
    setAttached((a) => a.filter((v) => v.id !== id));
    asked.current.delete(id);
    salesCache.clearVaultCache(accountId, id);
    setItems((m) => { const n = { ...m }; delete n[id]; return n; });
    void track(() => api.detachSalesVault(accountId, id)).catch(() => {});
  };

  return { attached, attachedLoading, items, loadingVaults, error, credits, analysing, attach, detach, setField, describe };
}

